import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, RotateCw, ZoomIn, ZoomOut, Crop, Check, Maximize } from 'lucide-react';

const ASPECT_RATIOS = [
  { label: '3:4', value: 3 / 4, icon: 'Portrait' },
  { label: '1:1', value: 1, icon: 'Carré' },
  { label: '4:3', value: 4 / 3, icon: 'Paysage' },
  { label: '16:9', value: 16 / 9, icon: 'Bannière' },
  { label: 'Libre', value: null, icon: 'Libre' },
];

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      'image/jpeg',
      0.92
    );
  });
}

export default function ImageCropModal({ imageSrc, fileName, onCropDone, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(3 / 4);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      const ext = fileName?.split('.').pop()?.toLowerCase() || 'jpg';
      const croppedFile = new File([croppedBlob], `cropped_${fileName || 'image.jpg'}`, {
        type: 'image/jpeg',
      });
      onCropDone(croppedFile);
    } catch (e) {
      console.error('Crop error:', e);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" data-testid="crop-modal">
      {/* Modal */}
      <div className="bg-white w-full max-w-2xl mx-4 max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E0DFDD]">
          <div className="flex items-center gap-2">
            <Crop size={18} className="text-[#D4AF37]" />
            <h3 className="font-medium text-sm">Recadrer l'image</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-[#F5F4F2] rounded transition-colors"
            data-testid="crop-cancel-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Crop area */}
        <div className="relative flex-1 min-h-[350px] bg-[#1A1514]" data-testid="crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect || undefined}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={true}
            style={{
              containerStyle: { background: '#1A1514' },
              cropAreaStyle: { border: '2px solid #D4AF37' },
            }}
          />
        </div>

        {/* Controls */}
        <div className="px-5 py-4 bg-[#FAFAF9] border-t border-[#E0DFDD] space-y-4">
          {/* Aspect ratio */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#6B6A68] mr-1">Ratio :</span>
            {ASPECT_RATIOS.map((r) => (
              <button
                key={r.label}
                onClick={() => setAspect(r.value)}
                className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                  (aspect === r.value || (aspect === null && r.value === null))
                    ? 'bg-[#1A1514] text-white border-[#1A1514]'
                    : 'bg-white text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]'
                }`}
                data-testid={`aspect-${r.label}`}
              >
                {r.icon}
              </button>
            ))}
          </div>

          {/* Zoom & Rotation */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 flex-1">
              <ZoomOut size={14} className="text-[#6B6A68]" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 h-1.5 accent-[#D4AF37] cursor-pointer"
                data-testid="zoom-slider"
              />
              <ZoomIn size={14} className="text-[#6B6A68]" />
              <span className="text-xs text-[#6B6A68] w-10 text-right">{Math.round(zoom * 100)}%</span>
            </div>

            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#E0DFDD] hover:border-[#1A1514] transition-colors"
              data-testid="rotate-btn"
            >
              <RotateCw size={13} />
              {rotation}°
            </button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onCancel}
              className="px-6 py-2.5 text-sm border border-[#E0DFDD] hover:border-[#1A1514] transition-colors"
              data-testid="crop-skip-btn"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={processing}
              className="px-6 py-2.5 text-sm bg-[#1A1514] text-white hover:bg-[#D4AF37] transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="crop-confirm-btn"
            >
              {processing ? (
                <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              ) : (
                <Check size={15} />
              )}
              Recadrer & Uploader
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
